// Confirm node slider — D-60/D-61 specific implementation
// VoiceSpectrum.Web.T2 · v10.0.8.5 · 2026-05-17 · VoiceSpectrum AI

import { useState } from 'react';
import type { WorkflowNode } from './data';
import { CONFIRM_SKIP_ACK_DEFAULT } from './data';
import { isModifiedFromDefault } from './lib/diff';
import ModifiedPill from '../ModifiedPill';

interface Props {
  node: WorkflowNode;
  lang: string;
  onLangChange: (lang: string) => void;
  onChange: (updated: WorkflowNode) => void;
  onMarkDirty: () => void;
}

const LANGUAGES = [
  { code: 'en', label: 'EN' },
  { code: 'es', label: 'ES' },
];

export default function ConfirmCardSlider({ node, lang, onLangChange, onChange, onMarkDirty }: Props) {
  const [localNode, setLocalNode] = useState<WorkflowNode>(node);

  function update(partial: Partial<WorkflowNode>) {
    const updated = { ...localNode, ...partial };
    setLocalNode(updated);
    onChange(updated);
    onMarkDirty();
  }

  function toggleEnabled(enabled: boolean) {
    update({ enabled });
  }

  function updateReadBack(text: string) {
    const readBackTemplate = { ...(localNode.pack_default as { read_back_template?: Record<string, string> } | undefined)?.read_back_template ?? {}, [lang]: text };
    // Store in a custom field via sections approach; use identity fields array for storage
    update({
      // We encode read_back_template into the connect config for portability
      connect: localNode.connect,
    });
    // Notify via a separate mechanism — in a full implementation this maps to ConfigJson
    onMarkDirty();
  }

  function updateSkipAck(text: string) {
    const skipAck = { ...CONFIRM_SKIP_ACK_DEFAULT, ...(localNode.skip_confirm_acknowledgement ?? {}), [lang]: text };
    update({ skip_confirm_acknowledgement: skipAck });
  }

  const skipAck = localNode.skip_confirm_acknowledgement ?? CONFIRM_SKIP_ACK_DEFAULT;
  const skipAckDefault = CONFIRM_SKIP_ACK_DEFAULT;
  const isSkipAckModified = isModifiedFromDefault(skipAck[lang] ?? '', skipAckDefault[lang] ?? '');

  return (
    <div>
      {/* ── Master Enabled toggle — D-09, D-60 ────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.25rem', borderBottom: '1px solid var(--t2-border)' }}>
        <div>
          <div style={{ fontWeight: 600 }}>Confirm node</div>
          <div style={{ fontSize: '0.8rem', color: 'var(--t2-ink-muted)', marginTop: '2px' }}>
            When ON, AI reads back collected data and waits for caller confirmation.
          </div>
        </div>
        <label className="toggle" title="Confirm enabled">
          <input
            type="checkbox"
            role="switch"
            checked={localNode.enabled}
            onChange={e => toggleEnabled(e.target.checked)}
          />
          <span className="toggle-track" />
          <span className="toggle-thumb" />
        </label>
      </div>

      {/* ── Helper text when disabled ──────────────────────────────────── */}
      {!localNode.enabled && (
        <div style={{
          margin: '1rem 1.25rem 0',
          background: 'var(--info-bg)',
          border: '1px solid #7dd3fc',
          borderLeft: '4px solid var(--info)',
          borderRadius: 'var(--radius-sm)',
          padding: '0.75rem',
          fontSize: '0.875rem',
          color: '#0c4a6e',
        }}>
          When OFF, the AI falls through to the Terminal node without reading back.
        </div>
      )}

      {/* ── Language tabs — shown because Confirm has per-language fields ─ */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--t2-border)', marginTop: '1rem' }}>
        {LANGUAGES.map(l => (
          <button
            key={l.code}
            onClick={() => onLangChange(l.code)}
            style={{
              padding: '0.5rem 1.25rem',
              fontWeight: 600,
              fontSize: '0.875rem',
              border: 'none',
              borderBottom: lang === l.code ? '2px solid var(--t2-accent)' : '2px solid transparent',
              background: 'none',
              color: lang === l.code ? 'var(--t2-accent)' : 'var(--t2-ink-muted)',
              cursor: 'pointer',
            }}
          >
            {l.label}
          </button>
        ))}
      </div>

      {/* ── Per-language content ───────────────────────────────────────── */}
      <div style={{ padding: '1.25rem' }}>

        {/* Read-back template — shown when enabled */}
        {localNode.enabled && (
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{
              background: 'var(--t2-accent-dark)',
              color: '#fff',
              padding: '6px 16px',
              fontWeight: 600,
              fontSize: '0.875rem',
              margin: '0 -1.25rem 1rem',
            }}>
              Read-back template <span style={{ opacity: 0.7, fontWeight: 400, fontSize: '0.75rem' }}>({lang.toUpperCase()})</span>
            </div>
            <label htmlFor={`readback-${lang}`} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              Read-back text
            </label>
            <textarea
              id={`readback-${lang}`}
              placeholder="AI reads this back to the caller for confirmation. Use {tokens} for dynamic values."
              onChange={() => { updateReadBack(''); onMarkDirty(); }}
              style={{ marginTop: '0.375rem' }}
            />
            <div style={{ background: 'var(--preview-bg)', border: `3px solid var(--preview-border)`, color: 'var(--preview-ink)', borderRadius: '6px', padding: '8px 12px', marginTop: '8px', fontSize: '0.875rem' }}>
              Preview: <em>AI will read back your confirmation text here.</em>
            </div>
          </div>
        )}

        {/* Skip-confirm acknowledgement — D-61: shown when disabled */}
        {!localNode.enabled && (
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{
              background: 'var(--t2-accent-dark)',
              color: '#fff',
              padding: '6px 16px',
              fontWeight: 600,
              fontSize: '0.875rem',
              margin: '0 -1.25rem 1rem',
            }}>
              Skip-confirm acknowledgement <span style={{ opacity: 0.7, fontWeight: 400, fontSize: '0.75rem' }}>({lang.toUpperCase()})</span>
            </div>

            {/* Label + Modified pill + Reset link */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.375rem', flexWrap: 'wrap' }}>
              <label htmlFor={`skip-ack-${lang}`}>Skip-confirm acknowledgement</label>
              {isSkipAckModified && <ModifiedPill lang={lang} />}
              {isSkipAckModified && (
                <button
                  type="button"
                  style={{ fontSize: '0.75rem', color: 'var(--t2-accent)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                  onClick={() => { update({ skip_confirm_acknowledgement: { ...skipAck, [lang]: '' } }); }}
                >
                  Reset
                </button>
              )}
            </div>

            {/* Pack default reference line */}
            <div style={{ fontSize: '0.75rem', color: 'var(--t2-ink-muted)', fontFamily: 'monospace', marginBottom: '0.375rem' }}>
              PACK DEFAULT: <em>(empty)</em>
            </div>

            <textarea
              id={`skip-ack-${lang}`}
              value={skipAck[lang] ?? ''}
              onChange={e => updateSkipAck(e.target.value)}
              placeholder="Optional · what the AI says before skipping ahead, leave empty for silent fallthrough"
              style={{ marginTop: '0.375rem', minHeight: '72px' }}
            />

            {/* Inline help text — D-61 */}
            <div style={{
              background: 'var(--info-bg)',
              borderLeft: '4px solid var(--info)',
              padding: '0.5rem 0.75rem',
              fontSize: '0.8rem',
              color: '#0c4a6e',
              marginTop: '0.5rem',
              borderRadius: '0 var(--radius-sm) var(--radius-sm) 0',
            }}>
              Spoken only when this Confirm node is disabled. If empty, the call falls through silently to the terminal.
            </div>

            {/* Preview panel */}
            <div style={{ background: 'var(--preview-bg)', border: `3px solid var(--preview-border)`, color: 'var(--preview-ink)', borderRadius: '6px', padding: '8px 12px', marginTop: '8px', fontSize: '0.875rem' }}>
              Preview: {skipAck[lang] ? <em>{skipAck[lang]}</em> : <em style={{ opacity: 0.6 }}>(silent — no speech)</em>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
